import React, { useMemo, useRef, useState } from 'react';
import type { Post } from '@app/types/core';
import getContentSummary from '@core/utils/getContentSummary';
import { ScheduledPostsSummaryView } from '../../Shared/SchedulePost';
import {
  Box,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
} from '@ublend-npm/aulaui-core';
import ResponsivePostTags from '../../Shared/PostCard/ResponsivePostTags';
import { DeletePostDialog } from '../../Shared/PostCard';

export interface ScheduledPostsSummaryProps {
  canDeleteScheduledPost?: boolean;
  hasFinishedFetchingPosts?: boolean;
  reschedulePost?: (postData: any) => void;
  onOpenScheduledPostActions?: () => void;
  handlePublishPost?: (postId: string) => void;
  onViewScheduledPosts: (isMobile: boolean) => void;
  onDeleteScheduledPost?: (post: Post) => unknown;
  isUpdatingScheduledPost?: boolean;
  onChangeScheduledPost?: () => void;
  scheduledPost: Post | null;
  scheduledPostCount?: number;
}

const ATTACHMENT_SUMMARY_PATTERN = /^(?:📷|🎥|🎵|📁)\s/u;

const getScheduledPostContent = (post: Post) => {
  const summaries = (
    getContentSummary({ content: post.content, allBlocks: true }) || []
  )
    .filter(Boolean)
    .map((summary) => summary.trim());
  const attachmentSummaries = summaries.filter((summary) =>
    ATTACHMENT_SUMMARY_PATTERN.test(summary)
  );
  const attachmentCount = attachmentSummaries.length;
  const textSummaries = summaries.filter(
    (summary) => !ATTACHMENT_SUMMARY_PATTERN.test(summary)
  );
  const [title = '', ...previewBlocks] = textSummaries;

  return {
    attachmentCount,
    attachmentSummary:
      attachmentCount === 1 ? attachmentSummaries[0] : undefined,
    preview: previewBlocks.filter(Boolean).join(' ').trim(),
    title: title?.trim() || '',
  };
};

export const ScheduledPostsSummary = ({
  canDeleteScheduledPost = false,
  hasFinishedFetchingPosts,
  reschedulePost,
  onOpenScheduledPostActions,
  handlePublishPost,
  isUpdatingScheduledPost = false,
  onChangeScheduledPost,
  onDeleteScheduledPost,
  onViewScheduledPosts,
  scheduledPost,
  scheduledPostCount,
}: ScheduledPostsSummaryProps) => {
  // Get the current screen size
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const actionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const content = useMemo(
    () => (scheduledPost ? getScheduledPostContent(scheduledPost) : null),
    [scheduledPost]
  );

  if (
    !hasFinishedFetchingPosts ||
    !scheduledPost ||
    (typeof scheduledPostCount === 'number' && scheduledPostCount <= 0)
  ) {
    return null;
  }

  const restoreActionsFocus = () => {
    window.setTimeout(() => actionsButtonRef.current?.focus(), 0);
  };
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    restoreActionsFocus();
  };
  const confirmDelete = () => {
    if (isUpdatingScheduledPost || !onDeleteScheduledPost) return;

    setDeleteDialogOpen(false);
    Promise.resolve(onDeleteScheduledPost(scheduledPost)).then(
      restoreActionsFocus,
      restoreActionsFocus
    );
  };
  const canOpenDeleteActions =
    canDeleteScheduledPost && Boolean(onDeleteScheduledPost);

  return (
    <>
      <ScheduledPostsSummaryView
        actionsLabel="Scheduled post actions"
        attachmentCount={content?.attachmentCount || 0}
        attachmentSummary={content?.attachmentSummary}
        onChangeScheduledPost={(date: Date) => {
          if (reschedulePost) {
            reschedulePost({
              postId: scheduledPost?.objectId,
              content: scheduledPost?.content,
              scheduledFor: date,
            });
          }
        }}
        onOpenActions={
          canOpenDeleteActions
            ? (event) => {
                actionsButtonRef.current = event.currentTarget;
                setActionsAnchor(event.currentTarget);
              }
            : undefined
        }
        onPublishNow={() => {
          if (handlePublishPost) {
            handlePublishPost(scheduledPost.objectId);
          }
        }}
        onView={() => {
          if (onViewScheduledPosts) {
            onViewScheduledPosts(isMobile);
          }
        }}
        preview={content?.preview || ''}
        scheduledDate={scheduledPost.scheduledFor || null}
        summaryLabel="Next scheduled post"
        title={content?.title || ''}
        unavailableActionsLabel="Scheduled post actions unavailable"
        viewLabel={`View ${scheduledPostCount} scheduled post${
          scheduledPostCount === 1 ? '' : 's'
        }`}
        renderPostTags={
          <Box>
            <ResponsivePostTags post={scheduledPost} showAddTagIcon={false} />
          </Box>
        }
      />
      <Menu
        id="scheduled-post-actions"
        anchorEl={actionsAnchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(actionsAnchor)}
        onClose={() => setActionsAnchor(null)}
        onClick={(event) => event.stopPropagation()}
        slotProps={{
          paper: {
            style: { width: 160, maxWidth: 'calc(100vw - 32px)' },
          },
        }}
      >
        <MenuItem
          label="Delete"
          onClick={() => {
            setActionsAnchor(null);
            setDeleteDialogOpen(true);
          }}
        />
      </Menu>

      <DeletePostDialog
        loading={isUpdatingScheduledPost}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        open={deleteDialogOpen}
      />
    </>
  );
};

export default ScheduledPostsSummary;
